// ============================================
// Peptides 101 - progress persistence (localStorage only).
//
// Lets a visitor leave the guide and resume where they left off. Works
// logged out (no account, no DB). Crash-safe: any read/parse failure or
// unavailable storage falls back to "no saved progress" so the guide
// simply starts at the beginning. Cleared when the visitor exits through
// a door (library / get-started), which counts as finishing.
// ============================================

export interface LearnProgress {
  phase: 'primer' | 'chooser' | 'goal' | 'reassurance' | 'safety' | 'next';
  primerIndex: number;
  goalId: string | null;
}

const PHASES: ReadonlyArray<LearnProgress['phase']> = ['primer', 'chooser', 'goal', 'reassurance', 'safety', 'next'];

const KEY = 'prtcl.learn.v1';

export function loadLearnProgress(): LearnProgress | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as Partial<LearnProgress>;
    if (!p.phase || !PHASES.includes(p.phase)) return null;
    const primerIndex =
      typeof p.primerIndex === 'number' && Number.isFinite(p.primerIndex) ? Math.trunc(p.primerIndex) : 0;
    const goalId = typeof p.goalId === 'string' ? p.goalId : null;
    return { phase: p.phase, primerIndex, goalId };
  } catch {
    return null;
  }
}

export function saveLearnProgress(p: LearnProgress): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(p));
  } catch {
    /* storage unavailable (private mode / quota) - non-fatal */
  }
}

export function clearLearnProgress(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* non-fatal */
  }
}
